"use client"

import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { LoadingEllipsis } from "../../shared/components/loading/Loading"
import SingleSelect from "../../shared/components/single-select/SingleSelect"
import { useAppContext } from "../../shared/functions/Context"
import useTitle from "../../shared/hooks/useTitle"
import EmptyError from "../admin-settings/EmptyError"
import useBackButton from "../../shared/hooks/useBack"
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary"
import type User from "../../shared/models/User"
import { useNavigate } from "react-router-dom"
import { FileText, Briefcase, Building2, Calendar, ArrowRight, Search, MessageSquare, Users } from "lucide-react"

export interface ISubordinateDispute {
  userId: string
  userName: string | null
  disputedMeasuresCount: number
  jobTitle: string | null
  userDepartment: string
  submittedOn?: number
  midtermDisputes: number
  finalAssessmentDisputes: number
}

const SupervisorDisputeView = observer(() => {
  const { api, store, ui } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const navigate = useNavigate()
  const me = store.auth.me?.asJson

  const measures = store.measure.all
  const departments = store.department.all
  const allScorecards = store.individualScorecard.all

  useTitle("My Team Disputes")
  useBackButton()

  const onSearch = (employeeUid: string): void => setSearchValue(employeeUid)

  const sortByName = (a: User, b: User) => {
    return (a.asJson.displayName || "").localeCompare(b.asJson.displayName || "")
  }

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find((dept) => dept.asJson.id === departmentId)
    return department ? department.asJson.name : "Unknown Department"
  }

  const subordinates = store.user.all.filter((user) => user.asJson.supervisor === me?.uid)

  const getSubordinateDisputes = (subordinateIds: string[]): ISubordinateDispute[] => {
    // Filter scorecards that have disputes (either midterm or final assessment)
    // Include scorecards where isDispute is true OR where any measure has finalAssessmentIsDispute
    const subordinateMeasures = measures.filter((measure) => subordinateIds.includes(measure.asJson.uid))
    
    // Get unique user IDs that have any dispute (midterm or final assessment)
    const usersWithDisputes = new Set<string>()
    
    subordinateMeasures.forEach((measure) => {
      const hasMiddermDispute = measure.asJson.disputeReason || measure.asJson.isDispute
      const hasFinalDispute = measure.asJson.finalAssessmentDisputeReason || measure.asJson.finalAssessmentIsDispute
      
      if (hasMiddermDispute || hasFinalDispute) {
        usersWithDisputes.add(measure.asJson.uid)
      }
    })
    
    // Also include users whose scorecards have isDispute flag set
    allScorecards.forEach((scorecard) => {
      if (scorecard.asJson.isDispute && subordinateIds.includes(scorecard.asJson.uid)) {
        usersWithDisputes.add(scorecard.asJson.uid)
      }
    })

    const subordinateDisputeDetails: ISubordinateDispute[] = Array.from(usersWithDisputes).map((userId) => {
      // Count midterm disputes (using disputeReason or isDispute flag)
      const midtermDisputes = measures.filter(
        (measure) => measure.asJson.uid === userId && (measure.asJson.disputeReason || measure.asJson.isDispute),
      ).length

      // Count final assessment disputes (using finalAssessmentDisputeReason or finalAssessmentIsDispute flag)
      const finalAssessmentDisputes = measures.filter(
        (measure) => measure.asJson.uid === userId && (measure.asJson.finalAssessmentDisputeReason || measure.asJson.finalAssessmentIsDispute),
      ).length

      const disputedMeasuresCount = midtermDisputes + finalAssessmentDisputes

      const user = store.user.all.find((user) => user.asJson.uid === userId)
      const userName = user ? user.asJson.displayName : "Unknown User"
      const userDepartment = user ? getDepartmentName(user.asJson.department) : "Unknown Department"
      const jobTitle = user ? user.asJson.jobTitle : "Unknown Title"
      
      const scorecard = allScorecards.find((sc) => sc.asJson.uid === userId)

      return {
        userId,
        userName,
        disputedMeasuresCount,
        jobTitle,
        userDepartment,
        submittedOn: scorecard?.asJson.submittedOn,
        midtermDisputes,
        finalAssessmentDisputes,
      }
    })

    // Filter out users with no disputes
    return subordinateDisputeDetails.filter((detail) => detail.disputedMeasuresCount > 0)
  }

  const options = subordinates.sort(sortByName).map((user) => {
    return { label: user.asJson.displayName || "", value: user.asJson.uid }
  })

  const [subordinateDisputes, setSubordinateDisputes] = useState<ISubordinateDispute[]>([])

  useEffect(() => {
    const fetchData = async () => {
      const subordinateIds = subordinates.map((user) => user.asJson.uid)
      const disputeData = getSubordinateDisputes(subordinateIds)
      setSubordinateDisputes(disputeData)
      setLoading(false)
    }

    fetchData()
  }, [store.user.all, measures, allScorecards])

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      try {
        await api.individualScorecard.getAll()
        await api.user.getAll()
        await api.department.getAll()
        await api.division.getAll()
        await api.measure.getAll()
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        })
      }
      setLoading(false)
    }
    loadAll()
  }, [api.department, api.division, api.user, api.measure, api.individualScorecard, ui.snackbar])

  const filteredSubordinates = searchValue ? subordinateDisputes.filter((user) => user.userId === searchValue)
    : subordinateDisputes

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingEllipsis />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#3f6a8e] to-[#84cbe9] rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 text-white" />
              <h1 className="text-4xl font-bold text-white">My Team Disputes</h1>
            </div>
            <p className="text-blue-100 text-lg">Review and comment on disputes submitted by your direct reports</p>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5 text-gray-400" />
              <div className="flex-1">
                <SingleSelect
                  name="search-subordinate"
                  options={options}
                  width="100%"
                  onChange={onSearch}
                  placeholder="Search by team member name..."
                />
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-[#84cbe9]" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Team Members</h3>
              </div>
              <p className="text-3xl font-bold text-[#3f6a8e]">{subordinates.length}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <FileText className="w-5 h-5 text-[#faa05a]" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Team Disputes</h3>
              </div>
              <p className="text-3xl font-bold text-[#faa05a]">{subordinateDisputes.length}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Q2 (Midterm) Disputes</h3>
              </div>
              <p className="text-3xl font-bold text-purple-600">
                {subordinateDisputes.reduce((sum, user) => sum + user.midtermDisputes, 0)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-[#4bb543]" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Q4 (Final Assessment)</h3>
              </div>
              <p className="text-3xl font-bold text-[#4bb543]">
                {subordinateDisputes.reduce((sum, user) => sum + user.finalAssessmentDisputes, 0)}
              </p>
            </div>
          </div>

          {/* Subordinate Dispute Cards */}
          {filteredSubordinates.length > 0 ? (
            <div className="space-y-4">
              {filteredSubordinates.map((subordinate) => (
                <div
                  key={subordinate.userId}
                  onClick={() => navigate(`/c/scorecards/resolve/${subordinate.userId}`)}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#84cbe9] to-[#3f6a8e] flex items-center justify-center text-white font-semibold text-lg">
                        {subordinate.userName?.charAt(0) || "?"}
                      </div>

                      {/* User Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[#3f6a8e] mb-1">{subordinate.userName}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            <span>{subordinate.jobTitle}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            <span>{subordinate.userDepartment}</span>
                          </div>
                          {subordinate.submittedOn && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(subordinate.submittedOn).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Dispute Details */}
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-xs text-gray-600 mb-1">Q2 (Midterm)</p>
                          <p className="text-xl font-bold text-purple-600">{subordinate.midtermDisputes}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-600 mb-1">Q4 (Final)</p>
                          <p className="text-xl font-bold text-[#4bb543]">{subordinate.finalAssessmentDisputes}</p>
                        </div>
                        <div
                          className={`px-4 py-2 rounded-lg ml-4 ${
                            subordinate.disputedMeasuresCount > 5
                              ? "bg-red-100"
                              : subordinate.disputedMeasuresCount > 2
                                ? "bg-orange-100"
                                : "bg-green-100"
                          }`}
                        >
                          <p className="text-xs text-gray-600 mb-1">Total</p>
                          <p
                            className={`text-2xl font-bold ${
                              subordinate.disputedMeasuresCount > 5
                                ? "text-[#dc3545]"
                                : subordinate.disputedMeasuresCount > 2
                                  ? "text-[#faa05a]"
                                  : "text-[#4bb543]"
                            }`}
                          >
                            {subordinate.disputedMeasuresCount}
                          </p>
                        </div>
                        <ArrowRight className="w-6 h-6 text-gray-400 group-hover:text-[#84cbe9] group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
              <EmptyError errorMessage="No disputes found from your team members" />
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
})

export default SupervisorDisputeView
